import React from 'react';
import { cn } from '@/lib/cn';
import { Selection } from '@/components/ui';
import { useStore } from '@/providers/StoreProvider';
import { BATHROOM_VALUES, bathroomLabelFor, type BathroomValue } from './filterConfigs';
import { useAnalytics } from '@/hooks/useAnalytics';

export interface BathroomFilterProps {
  className?: string;
}

// Shared hook for bathroom filter logic - exported for reuse
export function useBathroomFilter() {
  const minNumBathrooms = useStore(s => s.listingsFilters.minNumBathrooms);
  const setMinNumBathrooms = useStore(s => s.listingsFilters.setMinNumBathrooms);
  const resetMinNumBathrooms = useStore(s => s.listingsFilters.resetMinNumBathrooms);

  const formatBathroomDisplay = () => {
    if (typeof minNumBathrooms !== 'number') {
      return undefined; // Show placeholder
    }

    return bathroomLabelFor(minNumBathrooms as BathroomValue);
  };

  return {
    minNumBathrooms,
    setMinNumBathrooms,
    resetMinNumBathrooms,
    bathroomValues: BATHROOM_VALUES,
    bathroomLabelFor,
    formatBathroomDisplay,
  };
}

export function BathroomFilter({ className }: BathroomFilterProps) {
  const {
    minNumBathrooms,
    setMinNumBathrooms,
    resetMinNumBathrooms,
    bathroomValues,
    bathroomLabelFor,
  } = useBathroomFilter();
  const analytics = useAnalytics();

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className={cn('flex w-full items-start gap-2')} role="group" aria-label="Bathrooms">
        {bathroomValues.map(val => {
          const selected = minNumBathrooms === val;
          return (
            <Selection
              key={val}
              selected={selected}
              onClick={() => {
                analytics.track('listings_filter_baths', { bath: val });
                if (selected) {
                  resetMinNumBathrooms();
                } else {
                  setMinNumBathrooms(val);
                }
              }}
              className={cn(
                // Layout and sizing
                'grow',
                // Text alignment
                'text-center',
              )}
            >
              {bathroomLabelFor(val)}
            </Selection>
          );
        })}
      </div>
    </div>
  );
}

export default BathroomFilter;
